import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ManagedSubdepartmentsApi } from "apis/ManagedSubdepartmentsApi";
import { ManagedSubdepartmentsSearchFilterQuery } from "models/filters/managedSubDepartments/ManagedSubdepartmentsSearchFilterQuery";
import { ManagedSubdepartment } from "models/dbModels/managedSubdepartments/ManagedSubdepartment";
import { ManagedStocksApi } from "apis/ManagedStocksApi";
import { BulkEditManagedStock } from "models/daoModels/managedStock/BulkEditManagedStock";

export const getSubDepartments = createAsyncThunk("subDepartmentSlice/getSubDepartments", async (departmentId?: number) => {
    const response = await ManagedSubdepartmentsApi.Search(departmentId ? { departmentIds: [departmentId] } : {});
    return response.data;
});

export const getSubDepartmentDetails = createAsyncThunk("subDepartmentSlice/getSubDepartmentDetails", async (subDepartmentId: number) => {
    const response = await ManagedSubdepartmentsApi.Search({ ids: [subDepartmentId] });
    return response.data;
});

export const deleteSubDepartment = createAsyncThunk("subDepartmentSlice/deleteSubDepartment", async (params: ManagedSubdepartmentsSearchFilterQuery) => {
    const response = await ManagedSubdepartmentsApi.Delete(params);
    return response.data;
});

export const createSubDepartment = createAsyncThunk("subDepartmentSlice/createSubDepartment", async (subdepartment: ManagedSubdepartment) => {
    const response = await ManagedSubdepartmentsApi.Add([subdepartment]);
    return response.data;
});

export const createSubDepartments = createAsyncThunk("subDepartmentSlice/createSubDepartments", async (subdepartment: ManagedSubdepartment[]) => {
    const response = await ManagedSubdepartmentsApi.Add(subdepartment);
    return response.data;
});

export const updateSubDepartment = createAsyncThunk("subDepartmentSlice/updateSubDepartment", async (subdepartment: ManagedSubdepartment) => {
    const response = await ManagedSubdepartmentsApi.Update([subdepartment]);
    return response.data;
});

export const updateSubDepartments = createAsyncThunk("subDepartmentSlice/updateSubDepartments", async (subdepartments: ManagedSubdepartment[]) => {
    const response = await ManagedSubdepartmentsApi.Update(subdepartments);
    return response.data;
});

export const importStockToSubdepartment = createAsyncThunk("stocksSlice/importStockToSubdepartment", async (params: BulkEditManagedStock) => {
    const response = await ManagedStocksApi.BulkUpdate(params);
    return response.data;
});

export const deleteStocksFromSubdepartment = createAsyncThunk("subDepartmentSlice/deleteStocksFromSubdepartment", async (params: BulkEditManagedStock) => {
    const response = await ManagedStocksApi.BulkUpdate(params);
    return response.data;
});

export const deleteStocksFromDepartment = createAsyncThunk("subDepartmentSlice/deleteStocksFromDepartment", async (params: BulkEditManagedStock) => {
    const response = await ManagedStocksApi.BulkUpdate(params);
    return response.data;
});

type InitialStateType = SubdepartmentStateModel & {
    table: ManagedSubdepartment[];
    showPopup: boolean;
};

type SubdepartmentStateModel = {
    id: number;
    departmentId: number;
    name: string;
    number: string;
    locked: boolean;
    modelId: string;
    loading: boolean;
    loadStocks: boolean;
    selectedRows: any[];
    selectedStockRows: any[];
    error: string;
}

const initialModel: SubdepartmentStateModel = {
    id: 0,
    departmentId: 0,
    name: "",
    number: "",
    locked: false,
    modelId: "",
    loading: false,
    loadStocks: false,
    selectedRows: [],
    selectedStockRows: [],
    error: "",
}

const initialState: InitialStateType = {
    ...initialModel,
    table: [],
    showPopup: false,
};

const subDepartmentsSlice = createSlice({
    name: "subDepartmentSlice",
    initialState: {
        ...initialState,
    },
    reducers: {
        updateDepartmentDetails: (state, action) => {
            return {
                ...state,
                id: action.payload.id,
                name: action.payload.name,
                locked: action.payload.locked,
            };
        },
        updateSubDepartmentField: (state, action) => {
            return {
                ...state,
                [action.payload.key]: action.payload.value,
            };
        },
        cleanSubDepartmentDetails: (state) => {
            return {
                ...state,
                ...initialModel,
            };
        },
    },
    extraReducers(builder) {
        builder.addCase(getSubDepartmentDetails.fulfilled, (state, action) => {
            state.id = action.payload.data[0].id;
            state.name = action.payload.data[0].name;
            state.locked = action.payload.data[0].locked;
            state.number = action.payload.data[0].number;
            state.loading = false;
        });
        builder.addCase(getSubDepartments.pending, (state, action) => {
            state.loading = true;
            state.table = [];
        });
        builder.addCase(getSubDepartments.fulfilled, (state, action) => {
            state.table = action.payload.data ? action.payload.data : [];
            state.loading = false;
        });
        builder.addCase(getSubDepartments.rejected, (state, action) => {
            state.loading = false;
            state.table = [];
            // @ts-ignore
            state.error = action.payload.error || "";
        });
    }
});

export const {
    updateDepartmentDetails,
    cleanSubDepartmentDetails,
    updateSubDepartmentField,
} = subDepartmentsSlice.actions;

export default subDepartmentsSlice.reducer;